import { FormError } from '@kcftech/design-system';
import {
    IAnalyticsModelResponse,
    IAssetResponse,
    ICorporationResponse,
    ILocationResponse,
    IMonitoringPointResponse,
    PropertyChild,
    PropertyValueType,
} from '@kcftech/prometheus-client';
import { AnalyticsModelDropdown } from 'components/analytics/AnalyticsModelDropdown';
import { AssetDropdown } from 'components/asset/AssetDropdown';
import { Dropdown, PrimitiveDropdownItem } from 'components/dropdown/Dropdown';
import { RadioButtonGroup } from 'components/forms/RadioButtonGroup';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { MonitoringPointDropdown } from 'components/monitoring_point/MonitoringPointDropdown';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import {
    booleanRadioOptions,
    getParameterInputRules,
    supportedPropertyValueTypes,
} from 'pages/Models/AssignedModalTab/shared';
import { useEffect, useMemo } from 'react';
import {
    Controller,
    RegisterOptions,
    UseFormReturn,
    useWatch,
    useFormState,
} from 'react-hook-form';
import styled from 'styled-components';
import { isModelAssignedToGroup } from 'utils/analytics_model';
import { identity } from 'utils/data';

export type AssignmentType = 'Asset' | 'Monitoring Point';

export type AnalyticsModelAssignmentFormData = {
    model?: IAnalyticsModelResponse;
    assignmentType?: AssignmentType;
    asset?: IAssetResponse;
    monitoringPoint?: IMonitoringPointResponse;
    parameters: Record<string, string | undefined>;
};

const Columns = styled.div`
    @media ${MediaQueries.largeAndUp} {
        display: grid;
        height: 100%;
        grid-template-columns: 1fr 1fr;
        column-gap: 4rem;
    }
`;

const Column = styled.div`
    overflow-y: auto;
    padding: 2rem;
`;

const assignmentTypeRules: RegisterOptions = {
    required: 'Please select an assignment type.',
};

const modelRules: RegisterOptions = {
    required: 'Please select a model.',
};

const assignmentTypeOptions: AssignmentType[] = ['Asset', 'Monitoring Point'];

const getAssetRules = (model: IAnalyticsModelResponse | undefined): RegisterOptions => ({
    required: 'Select an asset.',
    validate: async (asset?: IAssetResponse) =>
        model && asset && (await isModelAssignedToGroup(model, asset.Id))
            ? 'This model assignment already exists.'
            : undefined,
});

const getMonitoringPointRules = (
    model: IAnalyticsModelResponse | undefined,
): RegisterOptions => ({
    required: 'Select a monitoring point.',
    validate: async (monitoringPoint?: IMonitoringPointResponse) =>
        model &&
        monitoringPoint &&
        (await isModelAssignedToGroup(model, monitoringPoint.Id))
            ? 'This model assignment already exists.'
            : undefined,
});

const renderParameterInput = (
    form: UseFormReturn<AnalyticsModelAssignmentFormData>,
    property: PropertyChild,
) => {
    if (!supportedPropertyValueTypes.includes(property.PropertyValueType)) {
        return <></>;
    }

    const {
        control,
        formState: { errors },
        register,
    } = form;

    const parameterPath = `parameters.${property.PropertyId}` as const;
    const parameterErrors = errors.parameters?.[property.PropertyId.toString()];
    const rules = getParameterInputRules(property);

    switch (property.PropertyValueType) {
        case PropertyValueType.Double:
        case PropertyValueType.Float:
        case PropertyValueType.Integer:
        case PropertyValueType.String:
            return (
                <TextInput
                    placeholder={`Enter ${property.PropertyName} `}
                    className="my-10"
                    invalid={parameterErrors !== undefined}
                    {...register(parameterPath, rules)}
                />
            );
        case PropertyValueType.Boolean:
            return (
                <Controller
                    control={control}
                    name={parameterPath}
                    rules={rules}
                    render={({ field }) => (
                        <RadioButtonGroup
                            radioStyle="indicator"
                            layout="horizontal"
                            name={property.PropertyName}
                            items={booleanRadioOptions}
                            value={field.value}
                            onChange={field.onChange}
                            className="mt-20 mb-10"
                        />
                    )}
                />
            );
        default:
            return <></>;
    }
};

export type AnalyticsModelAssignmentFormProps = Readonly<{
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
    form: UseFormReturn<AnalyticsModelAssignmentFormData>;
}>;

export function AnalyticsModelAssignmentForm({
    corporation,
    location,
    form,
}: AnalyticsModelAssignmentFormProps): JSX.Element {
    const {
        control,
        setValue,
    } = form;

    const {
        errors,
    } = useFormState({
        control,
    });

    const assignmentType = useWatch({ control, name: 'assignmentType' });
    const model = useWatch({ control, name: 'model' });

    const filteredProperties = useMemo(
        () =>
            model?.Properties.filter((property) =>
                supportedPropertyValueTypes.includes(property.PropertyValueType),
            ),
        [model?.Properties],
    );

    useEffect(() => {
        setValue('asset', undefined);
        setValue('monitoringPoint', undefined);
    }, [corporation, location, assignmentType, setValue]);

    useEffect(() => {
        setValue('parameters', {});
    }, [model, setValue]);

    return (
        <Columns>
            <Column>
                <ValidatableLabel
                    className="mb-20"
                    label="Analytics Model"
                    required
                    error={errors.model}
                >
                    <Controller
                        name="model"
                        control={control}
                        rules={modelRules}
                        render={({ field }) => (
                            <AnalyticsModelDropdown
                                corporationId={corporation?.Id}
                                locationId={location?.Id}
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </ValidatableLabel>

                <ValidatableLabel
                    className="mb-20"
                    label="Assignment Type"
                    required
                    error={errors.assignmentType}
                >
                    <Controller
                        name="assignmentType"
                        control={control}
                        rules={assignmentTypeRules}
                        render={({ field }) => (
                            <Dropdown<AssignmentType>
                                dataKey={identity}
                                data={assignmentTypeOptions}
                                value={field.value}
                                renderLabel={({ value }) =>
                                    value ?? 'Select an assignment type'
                                }
                                renderItem={PrimitiveDropdownItem}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </ValidatableLabel>

                {assignmentType === 'Asset' && (
                    <ValidatableLabel
                        className="mb-20"
                        label="Asset"
                        required
                        error={errors.asset}
                    >
                        <Controller
                            name="asset"
                            control={control}
                            rules={getAssetRules(model)}
                            render={({ field }) => (
                                <AssetDropdown
                                    value={field.value}
                                    onChange={field.onChange}
                                    locationId={location?.Id}
                                    corporationId={corporation?.Id}
                                />
                            )}
                        />
                    </ValidatableLabel>
                )}

                {assignmentType === 'Monitoring Point' && (
                    <ValidatableLabel
                        className="mb-20"
                        label="Monitoring Point"
                        required
                        error={errors.monitoringPoint}
                    >
                        <Controller
                            name="monitoringPoint"
                            control={control}
                            rules={getMonitoringPointRules(model)}
                            render={({ field }) => (
                                <MonitoringPointDropdown
                                    corporationId={corporation?.Id}
                                    locationId={location?.Id}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </ValidatableLabel>
                )}
            </Column>
            <Column>
                {model ? (
                    <>
                        {filteredProperties && filteredProperties.length > 0 ? (
                            filteredProperties.map((property) => (
                                <ValidatableLabel
                                    key={property.PropertyId}
                                    className="mb-20"
                                    label={property.PropertyName}
                                    required={property.PropertyRequired}
                                    error={errors.parameters?.[property.PropertyName]}
                                >
                                    {renderParameterInput(form, property)}
                                    <FormError
                                        message={
                                            errors.parameters?.[
                                                property.PropertyId.toString()
                                            ]?.message
                                        }
                                    />
                                </ValidatableLabel>
                            ))
                        ) : (
                            <div>No input needed for this model</div>
                        )}
                    </>
                ) : (
                    <div>Select a model to configure parameters.</div>
                )}
            </Column>
        </Columns>
    );
}
